import { AxiosInstance, isAxiosError } from "axios";
import { IEventList } from "../interfaces/IEventList";
import { buildPagedFilterQuery } from "../utils/queryHelpers";
import { IPagedFilterRequest } from "../interfaces/IPagedFilterResults";
import { ITicketAllocationCheck } from "../interfaces/ITicketAllocationCheck";
import { ITicket } from "../interfaces/IHome";
import { IWeeklyAgendaEvent } from "../interfaces/IHome";
import { IParticipantTicketStats } from "../interfaces/IParticipantTicketStats";
import { IUserRegistration } from "../interfaces/IUserRegistration";
import { IPagedFilterResponse } from "../interfaces/IPagedFilterResponse";
import { IParticipantEventFile } from "../interfaces/IParticipantEventFile";

export interface TicketWalletAvailability {
    google: boolean;
    apple: boolean;
}

export class ParticipantEventService {
    private apiClient: AxiosInstance;
    private endpoint: string;

    constructor(apiClient: AxiosInstance, endpoint: string = 'participant/event') {
        this.apiClient = apiClient;
        this.endpoint = endpoint;
    }

    async getAll(request: IPagedFilterRequest): Promise<IPagedFilterResponse<IEventList>> {
        try {
            const query = buildPagedFilterQuery(request);
            const response = await this.apiClient.get(`${this.endpoint}?${query}`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }

    async getUserRegistration(id: string): Promise<IUserRegistration> {
        try {
            const response = await this.apiClient.get(`${this.endpoint}/${id}/user-registration`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }

    async getSharedFiles(id: string): Promise<IParticipantEventFile[]> {
        try {
            const response = await this.apiClient.get(`${this.endpoint}/${id}/files`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }

    async getSharedFileUrl(eventId: string, fileId: string, isPreview = false): Promise<string | undefined> {
        try {
            const response = await this.apiClient.get(`${this.endpoint}/${eventId}/files/${fileId}/url?forPreview=${isPreview}`);
            return response.data;
        } catch (error: any) {
            if (error.response?.status === 404) {
                return undefined;
            }
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }

    async getFavourites(req: IPagedFilterRequest): Promise<IPagedFilterResponse<IEventList>> {
        try {
            const query = buildPagedFilterQuery(req);
            const response = await this.apiClient.get(`${this.endpoint}/favourites?${query}`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }

    async getWaitlist(req: IPagedFilterRequest): Promise<IPagedFilterResponse<IEventList>> {
        try {
            const query = buildPagedFilterQuery(req);
            const response = await this.apiClient.get(`${this.endpoint}/waitlists?${query}`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }

    async getTicket(req: IPagedFilterRequest): Promise<IPagedFilterResponse<ITicket>> {
        try {
            const query = buildPagedFilterQuery(req);
            const response = await this.apiClient.get(`${this.endpoint}/ticket?${query}`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data || 'An error occurred while fetching favourites');
        }
    }

    async getPendingTickets(): Promise<ITicket[]> {
        try {
            const response = await this.apiClient.get(`${this.endpoint}/ticket/pending-tickets`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data || 'An error occurred while fetching pending tickets');
        }
    }

    async getWeeklyAgenda(weekStart: Date, weekEnd: Date): Promise<IWeeklyAgendaEvent[]> {
        try {
            const response = await this.apiClient.get(`${this.endpoint}/ticket/weekly-agenda`, {
                params: {
                    weekStart: weekStart.toISOString(),
                    weekEnd: weekEnd.toISOString(),
                },
            });
            return Array.isArray(response.data) ? response.data as IWeeklyAgendaEvent[] : [];
        } catch (error: any) {
            throw new Error(error.response?.data || 'An error occurred while fetching the weekly agenda');
        }
    }

    async getTicketStats(category: string): Promise<IParticipantTicketStats> {
        try {
            const response = await this.apiClient.get(`${this.endpoint}/ticket/ticket-stats?category=${category}`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data || 'An error occurred while fetching favourites');
        }
    }

    async assignTicket(ticketId: string, payload: {
        firstName: string;
        lastName: string;
        email: string;
        eventId: string;
        ticketId: string;
    }) {
        return this.apiClient.post(`${this.endpoint}/ticket/${ticketId}/assign-ticket`, payload);
    }

    async unAssignTicket(ticketId: string, payload: {
        firstName: string;
        lastName: string;
        email: string;
        eventId: string;
        ticketId: string;
    }) {
        return this.apiClient.post(`${this.endpoint}/ticket/${ticketId}/unassign-ticket`, payload);
    }

    async acceptTicket(ticketId: string): Promise<ITicket> {
        const response = await this.apiClient.post(`${this.endpoint}/ticket/${ticketId}/accept-ticket`);
        return response.data;
    }

    async declineTicket(ticketId: string) {
        return this.apiClient.post(`${this.endpoint}/ticket/${ticketId}/decline-ticket`);
    }

    async requestRefund(ticketId: string, payload: {
        issuedTicketId: string;
        reason?: string;
        reasonType?: 'Voluntary' | 'EventRescheduled';
    }) {
        return this.apiClient.post(`${this.endpoint}/ticket/${ticketId}/refund`, payload);
    }

    async cancelRefund(ticketId: string) {
        return this.apiClient.delete(`${this.endpoint}/ticket/${ticketId}/refund`);
    }

    async getTicketAllocationInfo(ticketId: string, email: string, eventId: string): Promise<ITicketAllocationCheck> {
        const params = new URLSearchParams({
            email,
            eventId,
        });
        try {
            const response = await this.apiClient.get(`${this.endpoint}/ticket/${ticketId}/allocation-info?${params.toString()}`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }

    async addToFavourites(eventId: string): Promise<void> {
        try {
            await this.apiClient.post(`${this.endpoint}/favourites`, eventId, {
                headers: { 'Content-Type': 'application/json' }
            });
        } catch (error: any) {
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }
    async removeFromFavourites(eventId: string): Promise<void> {
        try {
            await this.apiClient.delete(`${this.endpoint}/favourites/${eventId}`);
        } catch (error: any) {
            throw new Error(error.response?.data || 'An error occurred');
        }
    }

    async getWalletAvailability(ticketId: string, signal: AbortSignal): Promise<TicketWalletAvailability> {
        const { data } = await this.apiClient.get<TicketWalletAvailability>(`${this.endpoint}/ticket/${encodeURIComponent(ticketId)}/wallet`, {
            signal, timeout: 15000,
        });
        if (!data || typeof data.google !== 'boolean' || typeof data.apple !== 'boolean') {
            throw new Error('Invalid wallet availability');
        }
        return { google: data.google, apple: data.apple };
    }

    async getGoogleWalletUrl(ticketId: string, signal: AbortSignal): Promise<string> {
        const { data } = await this.apiClient.post<{ url: string; expiresAt: string }>(`${this.endpoint}/ticket/${encodeURIComponent(ticketId)}/wallet/google`, null, {
            signal, timeout: 30000,
        });
        if (!data || typeof data.url !== 'string' || typeof data.expiresAt !== 'string' ||
            !Number.isFinite(Date.parse(data.expiresAt)) || Date.parse(data.expiresAt) <= Date.now()) {
            throw new Error('Invalid wallet link');
        }
        const url = new URL(data.url);
        if (url.origin !== 'https://pay.google.com' || url.username || url.password || url.search || url.hash ||
            !/^\/gp\/v\/save\/[A-Za-z0-9._-]+$/.test(url.pathname)) {
            throw new Error('Invalid wallet link');
        }
        return url.href;
    }

    async getAppleWalletPass(ticketId: string, signal: AbortSignal): Promise<Blob> {
        const { data } = await this.apiClient.post<Blob>(`${this.endpoint}/ticket/${encodeURIComponent(ticketId)}/wallet/apple`, null, {
            signal, timeout: 30000, responseType: 'blob',
        });
        if (!(data instanceof Blob) || data.type.toLowerCase() !== 'application/vnd.apple.pkpass' || data.size === 0 || data.size > 10 * 1024 * 1024) {
            throw new Error('Invalid wallet pass');
        }
        const signature = new Uint8Array(await data.slice(0, 4).arrayBuffer());
        if (signature[0] !== 0x50 || signature[1] !== 0x4b || signature[2] !== 0x03 || signature[3] !== 0x04) {
            throw new Error('Invalid wallet pass');
        }
        return data;
    }

    async getQrCode(ticketId: string): Promise<Blob> {
        try {
            const response = await this.apiClient.get(`${this.endpoint}/ticket/${ticketId}/qrcode`, {
                responseType: 'blob',
            });

            // Optional: Safety check 
            if (!(response.data instanceof Blob)) {
                throw new Error('Expected a blob but got something else');
            }

            return response.data;
        } catch (error: unknown) {
            if (isAxiosError(error) && error.response?.data instanceof Blob) {
                try {
                    const raw = await error.response.data.text();
                    error.response.data = JSON.parse(raw);
                } catch {
                    throw error;
                }
            }
            throw error;
        }
    }

    async getEventGroups(): Promise<{ role: string; count: number }[]> {
        try {
            const response = await this.apiClient.get(`${this.endpoint}/group-counts`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data || 'An error occurred');
        }
    }
}